import {
  MouseEventHandler,
  TouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBreakpoint } from "hooks/useBreakpoint";
import { throttle } from "utils/throttle";
import {
  findClosest,
  generateDistances,
  generateDistancesMobile,
} from "./utils";

type UseDragSliderParams = {
  len: number;
  defaultIndex?: number;
};

export const useDragSlider = ({ len, defaultIndex }: UseDragSliderParams) => {
  const { isCustomBreakpointSmaller } = useBreakpoint(1180);
  const [activeCard, setActiveCard] = useState(0);
  const [disabledPrev, setDisabledPrev] = useState(true);
  const [disabledNext, setDisabledNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState<number | null>(null);
  const [mouseMoved, setMouseMoved] = useState(0);
  const [widthContainer, setWidthContainer] = useState(0);
  const refItems = useRef<HTMLDivElement>(null);
  const refContainer = useRef<HTMLDivElement>(null);
  const distanceCards = useMemo(
    () =>
      isCustomBreakpointSmaller
        ? generateDistancesMobile(len, widthContainer)
        : generateDistances(len, widthContainer),
    [len, isCustomBreakpointSmaller, widthContainer],
  );

  useEffect(() => {
    if (refContainer.current) {
      setWidthContainer(refContainer.current.getBoundingClientRect().width);
    }
  }, [refContainer]);

  useEffect(() => {
    if (defaultIndex) {
      setActiveCard(defaultIndex);
    }
  }, [defaultIndex]);

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!refItems?.current) {
      return;
    }

    setIsDragging(true);
    setStartX(e.pageX - refItems.current.offsetLeft);
    setScrollLeft(refItems.current.scrollLeft);
    setMouseMoved(0);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (!refItems?.current) {
      return;
    }
    setIsDragging(true);
    setStartX(e.touches[0].pageX - refItems.current.offsetLeft);
    setScrollLeft(refItems.current.scrollLeft);
    setMouseMoved(0);
  };

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isDragging || !refItems?.current) {
      return;
    }

    const currentMousePositionInsideContainer =
      e.pageX - refItems.current.offsetLeft;
    setMouseMoved(currentMousePositionInsideContainer - startX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !refItems?.current) {
      return;
    }

    const currentMousePositionInsideContainer =
      e.touches[0].pageX - refItems.current.offsetLeft;
    setMouseMoved(currentMousePositionInsideContainer - startX);
  };

  const handleStopDragging = () => {
    setIsDragging(false);
    if (refItems?.current) {
      const distance = distanceCards[activeCard];
      refItems.current.scrollTo({
        left: distance,
        behavior: "smooth",
      });
    }
  };

  const onHandleNext = () => {
    if (activeCard === len - 1) {
      return;
    }
    const newActiveCard = activeCard + 1;
    setActiveCard(newActiveCard);
    if (newActiveCard === len - 1) {
      setDisabledNext(true);
    }
    setDisabledPrev(false);
  };

  const onHandlePrev = () => {
    if (activeCard === 0) {
      return;
    }
    const newActiveCard = activeCard - 1;
    setActiveCard(newActiveCard);
    if (newActiveCard === 0) {
      setDisabledPrev(true);
    }
    setDisabledNext(false);
  };

  useEffect(() => {
    if (refItems?.current && scrollLeft !== null) {
      refItems.current.scrollLeft = scrollLeft - mouseMoved;
      const value = findClosest(distanceCards, refItems.current.scrollLeft);
      setActiveCard(distanceCards?.findIndex((inner) => inner === value));
    }
  }, [
    refItems,
    scrollLeft,
    mouseMoved,
    isCustomBreakpointSmaller,
    distanceCards,
  ]);

  useEffect(() => {
    if (refItems?.current) {
      const distance = distanceCards[activeCard];
      refItems.current.scrollTo({
        left: distance,
        behavior: "smooth",
      });
    }
  }, [activeCard, distanceCards]);

  useEffect(() => {
    if (activeCard === 0) {
      setDisabledPrev(true);
    } else {
      setDisabledPrev(false);
    }
    if (activeCard === len - 1) {
      setDisabledNext(true);
    } else {
      setDisabledNext(false);
    }
  }, [activeCard, len]);

  return {
    activeCard,
    disabledPrev,
    disabledNext,
    onHandleNext: throttle(onHandleNext, 100),
    onHandlePrev: throttle(onHandlePrev, 100),
    refContainer,
    refItems,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleStopDragging,
    handleTouchStart,
    handleTouchMove,
  };
};
